import { parseDate } from "@internationalized/date";
import { CaretLeft, CaretRight, X } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import type { ItemLite } from "@plannello/shared";
import type { RefObject } from "react";
import {
  Button as AriaButton,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  Dialog,
  Heading,
  RangeCalendar,
} from "react-aria-components";
import { Popover } from "@/components/ui";
import { titleTooltip, useTitles } from "@/data/titles";
import { cx } from "@/lib/cx";

const navButton =
  "flex size-7 items-center justify-center rounded-control text-fg-muted outline-hidden data-hovered:bg-hover data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:outline-focus";

/**
 * "Set dates" for a timeline bar or an unscheduled item: a range calendar (pick start, then due;
 * the same day twice for a one-day item), fully keyboard operable, plus "Clear dates". The
 * keyboard alternative to dragging bars.
 */
export function SetDatesPopover({
  item,
  triggerRef,
  isOpen,
  onOpenChange,
  onChange,
}: {
  item: ItemLite | null;
  triggerRef: RefObject<HTMLElement | null>;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (start: string | null, due: string | null) => void;
}) {
  const titleOf = useTitles();
  if (!item) return null;
  const shown = titleOf(item);
  const start = item.startDate ?? item.dueDate;
  const end = item.dueDate ?? item.startDate;
  const value = start && end ? { start: parseDate(start), end: parseDate(end) } : null;
  return (
    <Popover
      triggerRef={triggerRef}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      placement="bottom start"
      className="p-0"
    >
      <Dialog aria-label={m.tl_set_dates_for({ key: item.key })} className="flex flex-col gap-2 p-3 outline-none">
        {({ close }) => (
          <>
            <p className="max-w-64 truncate-title text-xs text-fg-muted" title={titleTooltip(shown, item.title)}>
              <span className="font-mono">{item.key}</span> {shown.text}
            </p>
            <RangeCalendar
              aria-label={m.tl_set_dates_for({ key: item.key })}
              defaultValue={value}
              defaultFocusedValue={value?.start}
              onChange={(r) => {
                onChange(r.start.toString(), r.end.toString());
                close();
              }}
              className="w-64"
            >
              <header className="mb-2 flex items-center gap-1">
                <AriaButton slot="previous" className={navButton}>
                  <CaretLeft size={14} aria-hidden="true" />
                </AriaButton>
                <Heading className="flex-1 text-center text-sm font-medium text-fg" />
                <AriaButton slot="next" className={navButton}>
                  <CaretRight size={14} aria-hidden="true" />
                </AriaButton>
              </header>
              <CalendarGrid className="w-full border-collapse">
                <CalendarGridHeader>
                  {(day) => (
                    <CalendarHeaderCell className="pb-1 text-2xs font-medium text-fg-subtle">{day}</CalendarHeaderCell>
                  )}
                </CalendarGridHeader>
                <CalendarGridBody>
                  {(date) => (
                    <CalendarCell
                      date={date}
                      className={cx(
                        "my-px flex size-8 cursor-default items-center justify-center text-sm text-fg outline-none tnum",
                        "data-hovered:bg-hover data-outside-month:text-fg-subtle data-outside-month:opacity-50",
                        "data-selected:bg-accent-soft data-selected:text-accent-soft-fg",
                        "data-selection-start:rounded-l-control data-selection-start:bg-accent data-selection-start:text-accent-fg",
                        "data-selection-end:rounded-r-control data-selection-end:bg-accent data-selection-end:text-accent-fg",
                        "data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:outline-focus",
                        "data-today:font-semibold data-today:underline data-today:underline-offset-4",
                      )}
                    />
                  )}
                </CalendarGridBody>
              </CalendarGrid>
            </RangeCalendar>
            <p className="text-2xs text-fg-subtle">{m.tl_set_dates_hint()}</p>
            {item.startDate || item.dueDate ? (
              <AriaButton
                onPress={() => {
                  onChange(null, null);
                  close();
                }}
                className="flex h-(--control-h-sm) items-center gap-1.5 self-start rounded-control px-2 text-xs text-fg-muted outline-hidden data-hovered:bg-hover data-hovered:text-fg data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:outline-focus"
              >
                <X size={12} aria-hidden="true" />
                {m.tl_clear_dates()}
              </AriaButton>
            ) : null}
          </>
        )}
      </Dialog>
    </Popover>
  );
}
